import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AirOutlined,
  ChevronLeft,
  ChevronRight,
  Inventory2Outlined,
  PictureAsPdfOutlined,
  RestartAlt,
} from '@mui/icons-material';
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from '@mui/material';
import { WideLayout } from '../../../../../shared/layout/widelayout';
import { useResponsive } from '../../../../../hooks/useResponsive';
import { Spinner } from '../../../../../shared/ui/Spinners';
import { downloadPDF, getPdfDownloadUrl } from '../../../../../utils/documents';
import { getLastFromSplit } from '../../../../../utils/text';
import { useApiQuestionnaire } from './basketApi';
import { firstMissing, modelChoiceOf, tabOf } from './flow';
import { ModelPicker } from './ModelPicker';
import { useProduct, useProductGuides, useProductImage } from './productApi';
import { QuestionBlockView } from './QuestionBlockView';
import { BasketResponseDTO, DbProduct, FlowTab } from './types';
import {
  ExtraCard,
  ExtrasBlock,
  ExtrasEmpty,
  ExtrasHeader,
  ExtrasRail,
  ExtrasTitle,
  FitBody,
  FitHead,
  RailButtons,
} from './variantStyle';
import { SelectedItem } from './flow';
import { useQuestionnaire } from './useQuestionnaire';
import {
  Card,
  CardTabsBar,
  ContentGrid,
  EmptyState,
  FileGrid,
  FileTile,
  ImageBox,
  ModelDialog,
  NavButton,
  NavFooter,
  PageShell,
  PageTitle,
  ProductBody,
  ProductCard,
  ProductColumn,
  ProductHead,
  ProductInfo,
  ProductName,
  ProductText,
  ProgressFill,
  ProgressTrack,
  QuestionColumn,
  SectionDivider,
  SectionTab,
  SectionTabs,
  StepBody,
  StepHeader,
  StepLabel,
  TabBar,
  TabButton,
  TextAction,
} from './styles';

/** Paciente fixo enquanto a tela não recebe o pedido em andamento. */
const PATIENT_NAME = 'Mariana Oliveira';

const TABS: Array<{ id: FlowTab; label: string }> = [
  { id: 'principal', label: 'Terapia Principal - Acessórios' },
  { id: 'complementar', label: 'Terapias complementares' },
];

const contextLine = (productId: number, product: DbProduct | null) =>
  [
    PATIENT_NAME,
    product?.description ?? `Produto #${productId}`,
    product?.product_category?.description,
  ]
    .filter(Boolean)
    .join(' · ');

// ─── Coluna da direita: descrição do equipamento ──────────────────────────────

/**
 * Como o DescriptionTile da implantação: foto, nome e textos descritivos do
 * equipamento (explanation, ou explanation1, e technical_information), mais os
 * PDFs na aba Arquivos.
 */
/** Foto do produto adicional; ícone neutro enquanto carrega ou sem foto. */
const ExtraImage = ({ sapCode }: { sapCode?: string }) => {
  const src = useProductImage(sapCode);
  return <div>{src ? <img src={src} alt="" /> : <Inventory2Outlined />}</div>;
};

const DescriptionPanel = ({
  productId,
  product,
  extras,
}: {
  productId: number;
  product: DbProduct | null;
  extras: SelectedItem[];
}) => {
  const [tab, setTab] = useState<'description' | 'files'>('description');
  const image = useProductImage(product?.sap_code);
  const files = useProductGuides(
    tab === 'files' ? product?.sap_code : undefined
  );
  const rail = useRef<HTMLDivElement>(null);
  const scrollRail = (direction: -1 | 1) =>
    rail.current?.scrollBy({ left: direction * 480, behavior: 'smooth' });

  const explanation =
    product?.explanation?.trim() || product?.explanation1?.trim() || '';
  const technical = product?.technical_information?.trim() ?? '';

  const tabBar = (
    <TabBar role="tablist">
      <TabButton
        type="button"
        role="tab"
        active={tab === 'description'}
        aria-selected={tab === 'description'}
        onClick={() => setTab('description')}
      >
        Descrição
      </TabButton>
      <TabButton
        type="button"
        role="tab"
        active={tab === 'files'}
        aria-selected={tab === 'files'}
        onClick={() => setTab('files')}
      >
        Arquivos
      </TabButton>
    </TabBar>
  );

  const filesContent = files.loading ? (
    <EmptyState>Carregando arquivos…</EmptyState>
  ) : files.guides.length === 0 ? (
    <EmptyState>Nenhum arquivo disponível.</EmptyState>
  ) : (
    <FileGrid>
      {files.guides.map(({ productCode, guide }) => (
        <FileTile
          key={`${productCode}-${guide}`}
          type="button"
          title={getLastFromSplit(guide, '_')}
          onClick={() => downloadPDF(getPdfDownloadUrl(productCode, guide))}
        >
          <PictureAsPdfOutlined />
          {getLastFromSplit(guide, '_')}
        </FileTile>
      ))}
    </FileGrid>
  );

  const head = (
    <ProductHead>
      <ImageBox aria-hidden="true">
        {image ? <img src={image} alt="" /> : <AirOutlined />}
      </ImageBox>
      <ProductInfo>
        <ProductName>
          {product?.description ?? `Produto #${productId}`}
        </ProductName>
        <ProductText sx={{ whiteSpace: 'pre-line' }}>
          {explanation || 'Nenhuma descrição disponível.'}
        </ProductText>
        {technical && (
          <>
            <SectionDivider />
            <ProductText sx={{ whiteSpace: 'pre-line' }}>
              {technical}
            </ProductText>
          </>
        )}
      </ProductInfo>
    </ProductHead>
  );

  return (
    <ProductCard>
      {tabBar}
      {tab === 'description' ? (
        <FitBody>
          <FitHead>{head}</FitHead>
          <ExtrasBlock>
            <SectionDivider sx={{ margin: '12px 0' }} />
            <ExtrasHeader>
              <ExtrasTitle>Produtos adicionais</ExtrasTitle>
              {extras.length > 0 && (
                <RailButtons>
                  <IconButton
                    aria-label="Anterior"
                    onClick={() => scrollRail(-1)}
                  >
                    <ChevronLeft />
                  </IconButton>
                  <IconButton
                    aria-label="Próximo"
                    onClick={() => scrollRail(1)}
                  >
                    <ChevronRight />
                  </IconButton>
                </RailButtons>
              )}
            </ExtrasHeader>
            {extras.length === 0 ? (
              <ExtrasEmpty>
                Os produtos escolhidos nas perguntas aparecem aqui.
              </ExtrasEmpty>
            ) : (
              <ExtrasRail ref={rail}>
                {extras.map((item) => (
                  <ExtraCard key={item.key}>
                    <ExtraImage sapCode={item.sapCode} />
                    <strong>{item.name}</strong>
                  </ExtraCard>
                ))}
              </ExtrasRail>
            )}
          </ExtrasBlock>
        </FitBody>
      ) : (
        <ProductBody>{filesContent}</ProductBody>
      )}
    </ProductCard>
  );
};

// ─── Diálogos ─────────────────────────────────────────────────────────────────

/** Confirmação para ações que apagam o que foi feito (reiniciar, restaurar). */
const ConfirmDialog = ({
  open,
  title,
  message,
  confirmLabel,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: () => void;
  onClose: () => void;
}) => (
  <ModelDialog open={open} onClose={onClose} aria-labelledby="confirm-title">
    <DialogTitle id="confirm-title">{title}</DialogTitle>
    <DialogContent>
      <ProductText sx={{ margin: 0 }}>{message}</ProductText>
    </DialogContent>
    <DialogActions sx={{ padding: '0 24px 20px', gap: '8px' }}>
      <NavButton onClick={onClose}>Cancelar</NavButton>
      <NavButton primary onClick={onConfirm}>
        {confirmLabel}
      </NavButton>
    </DialogActions>
  </ModelDialog>
);

/** Aviso do "Finalizar pedido" enquanto a finalização não está ligada. */
const NoticeDialog = ({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) => (
  <ModelDialog open={open} onClose={onClose} aria-labelledby="notice-title">
    <DialogTitle id="notice-title">Fluxo concluído</DialogTitle>
    <DialogContent>
      <ProductText sx={{ margin: 0 }}>
        Todas as perguntas obrigatórias foram respondidas. A finalização do
        pedido entra quando este fluxo substituir o da implantação.
      </ProductText>
    </DialogContent>
    <DialogActions sx={{ padding: '0 24px 20px' }}>
      <NavButton primary onClick={onClose}>
        Entendi
      </NavButton>
    </DialogActions>
  </ModelDialog>
);

// ─── Tela de pedido ───────────────────────────────────────────────────────────

type ScreenProps = {
  flow: BasketResponseDTO;
  productId: number;
  product: DbProduct | null;
  /** "Voltar". Padrão: página anterior. */
  onExit?: () => void;
};

/**
 * Questionário em duas abas (obrigatórias / complementares) à esquerda e a
 * descrição do equipamento à direita. Todos os cards ficam abertos; o botão
 * principal leva à primeira pendência ("Prosseguir") ou finaliza o pedido.
 */
const BasketFlowScreen = ({
  flow,
  productId,
  product,
  onExit,
}: ScreenProps) => {
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  const q = useQuestionnaire(flow);
  const tabs = useMemo(() => tabOf(flow.questions), [flow]);
  const extras = q.items;

  const [tab, setTab] = useState<FlowTab>('principal');
  const [picker, setPicker] = useState<{ code: string; option: string } | null>(
    null
  );
  const [confirmReset, setConfirmReset] = useState(false);
  const [finished, setFinished] = useState(false);
  const [scrollTarget, setScrollTarget] = useState<string | null>(null);

  const tabQuestions = q.visible.filter((item) => tabs.get(item.code) === tab);
  const answeredCount = tabQuestions.filter(
    (item) => q.answers[item.code]
  ).length;
  const missingInTab = tabQuestions.filter(
    (item) => item.required && !q.answers[item.code]
  ).length;
  const pending = firstMissing(q.visible, q.answers, tabs);

  const pickerQuestion = q.visible.find((item) => item.code === picker?.code);
  const pickerOption = pickerQuestion?.options.find(
    (item) => item.value === picker?.option
  );
  const pickerBasket = pickerOption ? modelChoiceOf(pickerOption) : undefined;

  // Rola até a pendência depois que a aba dela estiver na tela.
  useEffect(() => {
    if (!scrollTarget) return;
    q.scrollTo(scrollTarget);
    setScrollTarget(null);
  }, [scrollTarget, tab]);

  const proceed = () => {
    if (!pending) {
      setFinished(true);
      return;
    }
    setTab(tabs.get(pending.code) ?? 'principal');
    q.flag(pending.code);
    setScrollTarget(pending.code);
  };

  const reset = () => {
    q.reset();
    setTab('principal');
    setConfirmReset(false);
  };

  return (
    <PageShell>
      <PageTitle>
        Gerando seu pedido <small>{contextLine(productId, product)}</small>
      </PageTitle>

      <ContentGrid>
        <QuestionColumn>
          <Card>
            <CardTabsBar>
              <SectionTabs role="tablist" aria-label="Partes do pedido">
                {TABS.map((item) => (
                  <SectionTab
                    key={item.id}
                    type="button"
                    role="tab"
                    active={tab === item.id}
                    aria-selected={tab === item.id}
                    onClick={() => setTab(item.id)}
                  >
                    {item.label}
                  </SectionTab>
                ))}
              </SectionTabs>
              <TextAction
                startIcon={<RestartAlt />}
                disabled={Object.keys(q.answers).length === 0}
                onClick={() => setConfirmReset(true)}
              >
                Reiniciar
              </TextAction>
            </CardTabsBar>

            <StepHeader>
              <StepLabel>
                <strong>
                  {answeredCount} de {tabQuestions.length} respondidas
                </strong>
                <span>
                  {missingInTab > 0
                    ? `${missingInTab} obrigatória(s) pendente(s)`
                    : 'Tudo pronto'}
                </span>
              </StepLabel>
              <ProgressTrack>
                <ProgressFill
                  percent={
                    (answeredCount / Math.max(tabQuestions.length, 1)) * 100
                  }
                />
              </ProgressTrack>
            </StepHeader>

            <StepBody>
              {tabQuestions.length === 0 && (
                <EmptyState>
                  {tab === 'principal'
                    ? 'Este produto não tem perguntas obrigatórias.'
                    : 'Este produto não tem terapias complementares.'}
                </EmptyState>
              )}
              {tabQuestions.map((question) => (
                <QuestionBlockView
                  key={question.code}
                  question={question}
                  answers={q.answers}
                  models={q.models}
                  showError={q.errorFor === question.code}
                  blockRef={q.blockRef(question.code)}
                  onAnswer={(value) => q.answer(question, value)}
                  onPickModel={(option) =>
                    setPicker({ code: question.code, option })
                  }
                />
              ))}
            </StepBody>

            <NavFooter>
              <NavButton onClick={onExit ?? (() => navigate(-1))}>
                Voltar
              </NavButton>
              <NavButton primary onClick={proceed}>
                {pending ? 'Prosseguir' : 'Finalizar pedido'}
              </NavButton>
            </NavFooter>
          </Card>
        </QuestionColumn>

        <ProductColumn>
          <DescriptionPanel
            productId={productId}
            product={product}
            extras={extras}
          />
        </ProductColumn>
      </ContentGrid>

      <ConfirmDialog
        open={confirmReset}
        title="Reiniciar as perguntas?"
        message="Todas as respostas e modelos escolhidos serão apagados."
        confirmLabel="Reiniciar"
        onConfirm={reset}
        onClose={() => setConfirmReset(false)}
      />
      <NoticeDialog open={finished} onClose={() => setFinished(false)} />
      <ModelPicker
        basket={pickerBasket}
        title={pickerQuestion?.text ?? ''}
        selectedId={
          pickerQuestion && q.answers[pickerQuestion.code] === picker?.option
            ? q.models[pickerQuestion.code]
            : undefined
        }
        isMobile={isMobile}
        onSelect={(productIdChoice) => {
          if (pickerQuestion && picker) {
            q.answer(pickerQuestion, picker.option);
            q.setModel(pickerQuestion.code, productIdChoice);
          }
          setPicker(null);
        }}
        onClose={() => setPicker(null)}
      />
    </PageShell>
  );
};

/** Cabeçalho + mensagem, para carregando / erro / sem fluxo. */
const StatusScreen = ({
  productId,
  product,
  children,
}: {
  productId: number;
  product: DbProduct | null;
  children: ReactNode;
}) => (
  <PageShell>
    <PageTitle>
      Gerando seu pedido <small>{contextLine(productId, product)}</small>
    </PageTitle>
    <Card>
      <EmptyState>{children}</EmptyState>
    </Card>
  </PageShell>
);

/** Produto aberto sem `?product=<id>`: o que tem fluxo no banco. */
const DEFAULT_PRODUCT_ID = 6786;

/**
 * Tela de pedido montada com o JSON de GET /basket/:productId, sem
 * transformação. Os dados do equipamento vêm de GET /queryProducts.
 */
export const ConfigureEquipmentMockup2 = () => {
  const [searchParams] = useSearchParams();
  const requested = Number(searchParams.get('product'));
  const productId = requested > 0 ? requested : DEFAULT_PRODUCT_ID;

  const api = useApiQuestionnaire(productId);
  const { product } = useProduct(productId);

  const status = (content: ReactNode) => (
    <WideLayout
      outlet={
        <StatusScreen productId={productId} product={product}>
          {content}
        </StatusScreen>
      }
    />
  );

  if (api.status === 'loading' || api.status === 'idle') {
    return status(
      <>
        <Spinner />
        <br />
        Carregando o fluxo do banco…
      </>
    );
  }

  if (api.status === 'error') {
    return status(
      <>
        Não foi possível carregar o fluxo do banco.
        <br />
        <small>{api.error}</small>
        <br />
        <NavButton sx={{ mt: 2 }} onClick={api.retry}>
          Tentar novamente
        </NavButton>
      </>
    );
  }

  if (!api.data) {
    return status(
      'O banco não tem fluxo configurado para este produto (a API respondeu null).'
    );
  }

  return (
    <WideLayout
      outlet={
        <BasketFlowScreen
          key={productId}
          flow={api.data}
          productId={productId}
          product={product}
        />
      }
    />
  );
};

export default ConfigureEquipmentMockup2;
